/**
 * Path values in variables: one separator, chosen once.
 *
 * Forward slash is the standard. POSIX, URLs, and Windows file APIs all
 * accept it, so `C:\foo/bar` and a sibling `\a\b` both become `/` paths.
 * UNC paths (`\\server\share`) stay on backslash — that is the form Windows
 * can open. A click on Undo keeps the slashes the user typed, and that exact
 * text is not corrected again.
 */

import { classifyOpenableValue } from './openableValue';
import { joinPathLineBreaks } from './variableScope/resolve';

const STANDARD_SEPARATOR = '/';
const UNC_SEPARATOR = '\\';

const pins = new Set();

const pinKey = (variableId, slot, raw) =>
  `${variableId}\0${slot ?? ''}\0${String(raw ?? '')}`;

export const pinPathSeparators = (variableId, slot, raw) => {
  pins.add(pinKey(variableId, slot, raw));
};

export const isPathSeparatorPinned = (variableId, slot, raw) =>
  pins.has(pinKey(variableId, slot, raw));

export const resetPathSeparatorPins = () => {
  pins.clear();
};

const findTokenEnd = (source, innerStart) => {
  let depth = 0;
  for (let i = innerStart; i < source.length - 1; i += 1) {
    if (source[i] === '{' && source[i + 1] === '{') {
      depth += 1;
      i += 1;
      continue;
    }
    if (source[i] === '}' && source[i + 1] === '}') {
      if (depth === 0) return i;
      depth -= 1;
      i += 1;
    }
  }
  return -1;
};

/** `{{…}}` tokens (including math) are left untouched. */
const maskTokens = (value) => {
  let out = '';
  let i = 0;
  while (i < value.length) {
    if (value.startsWith('{{', i)) {
      const close = findTokenEnd(value, i + 2);
      if (close < 0) {
        out += value.slice(i);
        break;
      }
      out += 'REF';
      i = close + 2;
      continue;
    }
    out += value[i];
    i += 1;
  }
  return out;
};

const replaceSepOutsideTokens = (value, from, to) => {
  let out = '';
  let i = 0;
  while (i < value.length) {
    if (value.startsWith('{{', i)) {
      const close = findTokenEnd(value, i + 2);
      if (close < 0) {
        out += value.slice(i).split(from).join(to);
        break;
      }
      out += value.slice(i, close + 2);
      i = close + 2;
      continue;
    }
    const next = value.indexOf('{{', i);
    const end = next === -1 ? value.length : next;
    out += value.slice(i, end).split(from).join(to);
    i = end;
  }
  return out;
};

const hasSepOutsideTokens = (value, sep) => {
  let i = 0;
  while (i < value.length) {
    if (value.startsWith('{{', i)) {
      const close = findTokenEnd(value, i + 2);
      if (close < 0) return value.slice(i).includes(sep);
      i = close + 2;
      continue;
    }
    if (value[i] === sep) return true;
    i += 1;
  }
  return false;
};

const isUnc = (masked) => /^(?:\\\\|\/\/)[^\\/\s]+/.test(masked);

/** Two or more segments, the whole string — not a sentence and not `/math`. */
const isRelativePath = (masked) => {
  if (!masked || /\s/.test(masked)) return false;
  const parts = masked.split(/[\\/]/).filter((part) => part.length > 0);
  return parts.length >= 2 && /[\\/]/.test(masked);
};

/**
 * @returns {null | { family: 'fs'|'unc'|'bucket', hasFwd: boolean, hasBack: boolean, mixed: boolean }}
 */
const pathText = (raw) =>
  joinPathLineBreaks(String(raw ?? ''), { joinInternal: true });

export const inspectPathValue = (raw) => {
  const value = pathText(raw);
  if (!value || /[\r\n]/.test(value)) return null;
  const masked = maskTokens(value).trim();
  if (!masked) return null;

  const kind = classifyOpenableValue(masked).kind;
  if (kind === 'url') return null;

  const hasFwd = hasSepOutsideTokens(value, '/');
  const hasBack = hasSepOutsideTokens(value, '\\');
  if (!hasFwd && !hasBack) return null;

  let family = null;
  if (kind === 'bucket') family = 'bucket';
  else if (isUnc(masked)) family = 'unc';
  else if (kind === 'local-file' || kind === 'local-folder' || isRelativePath(masked)) {
    family = 'fs';
  }
  if (!family) return null;

  return { family, hasFwd, hasBack, mixed: hasFwd && hasBack };
};

const rewrite = (raw, info) => {
  const value = pathText(raw);
  if (info.family === 'unc') {
    if (!info.mixed) return value;
    return replaceSepOutsideTokens(value, '/', UNC_SEPARATOR);
  }
  if (!info.hasBack) return value;
  return replaceSepOutsideTokens(value, '\\', STANDARD_SEPARATOR);
};

/**
 * @param {Array<{ variableId: string, scope: string, slot: number|null, value: string, writable?: boolean, locked?: boolean }>} items
 * @returns {Array<{ variableId: string, scope: string, slot: number|null, previous: string, next: string }>}
 */
export const planPathSeparatorFixes = (items) => {
  const rows = (items || []).map((item) => ({
    item,
    info: inspectPathValue(item.value),
  }));

  const fs = rows.filter((row) => row.info?.family === 'fs');
  const fsConflict =
    fs.some((row) => row.info.mixed) ||
    (fs.some((row) => row.info.hasFwd) && fs.some((row) => row.info.hasBack));

  const fixes = [];
  for (const row of rows) {
    const { item, info } = row;
    if (!info) continue;
    if (item.locked || item.writable === false) continue;
    if (isPathSeparatorPinned(item.variableId, item.slot, item.value)) continue;

    let should = false;
    if (info.family === 'fs') should = fsConflict && info.hasBack;
    else if (info.family === 'unc') should = info.mixed;
    else if (info.family === 'bucket') should = info.hasBack;
    if (!should) continue;

    const next = rewrite(item.value, info);
    if (next === item.value) continue;
    fixes.push({
      variableId: item.variableId,
      scope: item.scope,
      slot: item.slot,
      previous: item.value,
      next,
    });
  }
  return fixes;
};
